"use client";
import { useEffect, useRef } from "react";

export type TemplateShowcaseItem = {
  image: string;
  alt?: string;
};

export type TemplateShowcaseSectionData = {
  items: TemplateShowcaseItem[];
  ctaText?: string;
  ctaLink?: string;
};

const CARD_WIDTH = 280;
const CARD_GAP = 16;

const InfiniteRow = ({
  images,
  direction,
  speed,
}: {
  images: string[];
  direction: "left" | "right";
  speed: number;
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const totalWidth = images.length * (CARD_WIDTH + CARD_GAP);
    const step = direction === "left" ? -speed / 60 : speed / 60;

    const animate = () => {
      posRef.current += step;
      if (posRef.current <= -totalWidth) posRef.current += totalWidth;
      if (posRef.current >= 0) posRef.current -= totalWidth;
      track.style.transform = `translateX(${posRef.current}px)`;
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [images.length, direction, speed]);

  const doubled = [...images, ...images];

  return (
    <div
      className="overflow-hidden"
      style={{
        maskImage: "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
      }}
    >
      <div ref={trackRef} className="flex" style={{ gap: CARD_GAP, willChange: "transform" }}>
        {doubled.map((src, i) => (
          <div
            key={i}
            className="flex-shrink-0 rounded-xl overflow-hidden"
            style={{ width: CARD_WIDTH, height: Math.round(CARD_WIDTH * 9 / 16) }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={src} alt="" className="w-full h-full object-cover object-top" />
          </div>
        ))}
      </div>
    </div>
  );
};

const TemplateShowcaseSection = ({
  items,
  ctaText = "Explore our templates",
  ctaLink = "#",
}: TemplateShowcaseSectionData) => {
  const row1 = items.slice(0, 5).map(i => i.image);
  const row2 = items.slice(5, 9).map(i => i.image);
  const row3 = items.slice(9, 16).map(i => i.image);

  return (
    <section className="w-full bg-white py-10 md:py-16">
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="relative overflow-hidden rounded-3xl bg-[#0D0D0D] py-12 md:py-16">

          {/* Rows with alternating scroll directions */}
          <div className="flex flex-col gap-4">
            <InfiniteRow images={row1} direction="left" speed={35} />
            <InfiniteRow images={row2} direction="right" speed={28} />
            <InfiniteRow images={row3} direction="left" speed={32} />
          </div>

          {/* Centered floating CTA */}
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <a
              href={ctaLink}
              className="flex items-center gap-3 rounded-[41px] bg-gradient-to-r from-[#4E5DE0] from-10% to-[#5868F7] to-100% px-8 py-3.5 font-inter text-[15px] font-semibold text-white shadow-lg transition-transform hover:scale-105"
            >
              {ctaText}
              <span>→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TemplateShowcaseSection;
